/**
 * Payload-validation rejection for the mock API.
 *
 * WHY THIS EXISTS: no msw handler emits a 400 or 422 at all. POSTs route to
 * `addToEntityListData`, which validates nothing and returns 201 to anything —
 * so dev, the e2e suite and the unit suite were all structurally incapable of
 * producing a rejected write. That is exactly how the 2026-08-15 production 400
 * shipped (Honeybadger 133565364 / 133565262: a user failed four times to add a
 * team member to a service group because the input spec omitted
 * `splitFraction`, which the API requires to be PRESENT).
 *
 * Direct precedent: `versionConflict.ts`, written after the identical blindness
 * let a stale-version 409 reach production. The lesson is the same one both
 * times — before concluding "this can't happen", check whether the mock is
 * capable of making it happen.
 *
 * The shape here mirrors the REAL Litestar/pydantic body, snake_case included,
 * because the whole point is that `describeErrorBody` (services/http/utils.ts)
 * is exercised against what the server actually sends rather than against a
 * convenient invention.
 */

export interface IValidationExtra {
  message: string
  key: string
}

/**
 * The 400/422 body, in Litestar's shape:
 * `{ status_code, detail, extra: [{ message, key }] }`.
 */
export function validationFailureBody({
  statusCode = 400,
  path,
  extra,
}: {
  statusCode?: number
  path: string
  extra: IValidationExtra[]
}) {
  return {
    status_code: statusCode,
    detail: `Validation failed for POST ${path}`,
    extra,
  }
}

/**
 * Which of `requiredKeys` are ABSENT from `entity`?
 *
 * 🔑 Absent is the failure; present-and-null is valid. That one distinction IS
 * the production defect, expressed as an assertion: in pydantic,
 * `split_fraction: float | None` with no default means the key must be present
 * and its value MAY be null, so `{}` is a 400 while `{ splitFraction: null }`
 * is accepted. Anything that tests for falsiness here would pass the broken
 * payload and reproduce nothing.
 */
export function missingRequiredFields(
  entity: Record<string, unknown> | undefined | null,
  requiredKeys: string[]
): IValidationExtra[] {
  if (!entity || typeof entity !== 'object') {
    return requiredKeys.map((key) => ({ message: 'Field required', key }))
  }
  return requiredKeys
    .filter((key) => !Object.prototype.hasOwnProperty.call(entity, key))
    .map((key) => ({ message: 'Field required', key }))
}
